// @ts-check

import { computed, ref, watch } from 'vue';

import { savedPartiesSchema } from '@/data/schema/parties';
import { useAssignRoles } from '@/composables/roles/roles';
import {
  createNewParties,
  createNewParty,
} from '@/store/modules/envelopes/factories/parties';

/** @typedef {import('@/data/schema/parties').TNewOrSavedParty} TUpdateParty */
/** @typedef {import('@/data/schema/parties').TNewOrSavedParties} TUpdateParties */
/** @typedef {import('@/data/schema/parties').TCreatingParties} TCreatingParties */
/** @typedef {import('@/data/schema/documents').TDocuments} TDocuments */

/**
 * Creates the initial parties to assign roles to
 * updates parties when documents change
 *
 * @param {import('vue').Ref<TCreatingParties>} currentParties
 * @param {import('vue').Ref<TDocuments>} documents
 */
export function useParties(currentParties, documents) {
  /** @type {import('vue').Ref<TUpdateParties>} parties */
  const parties = ref(savedPartiesSchema.parse(currentParties.value));

  const { assignOnlyRoleToOnlyParty } = useAssignRoles(parties, documents);

  // the maximum number of roles on a document, or at least 1
  const minParties = computed(() =>
    Math.max(1, ...documents.value.map((document) => document.parties.length)),
  );

  /**
   * Ensures the minimum required parties exist
   */
  function setParties() {
    const templateRoleIds = documents.value.flatMap((template) =>
      template.parties.map((party) => party.id),
    );

    // prune to remove no longer needed parties
    const prunedParties = parties.value
      .map((party) => {
        const extantIds = party.role_ids.filter((id) =>
          templateRoleIds.includes(id),
        );

        // mark empty parties to be pruned
        if (
          !(
            extantIds.length ||
            party.title ||
            party.email ||
            party.message ||
            party.password
          )
        ) {
          return undefined;
        }

        return { ...party, role_ids: extantIds };
      })
      .filter(/** @returns {party is TUpdateParty} */ (party) => !!party);

    // parties to add so we have the minimum required to assign all roles
    const newParties = createNewParties(
      minParties.value - prunedParties.length,
    );

    // combine the existing parties with new base parties and sort to put "copy"'s last
    parties.value = [...prunedParties, ...newParties].sort((a, b) => {
      if (a.role === 'copy') return 1;
      if (b.role === 'copy') return -1;
      return 0;
    });

    assignOnlyRoleToOnlyParty();
  }

  const canRemoveParties = computed(
    () => parties.value.length > minParties.value,
  );

  // refresh parties if documents change
  watch(documents, setParties, { immediate: true });

  function addParty() {
    if (!parties.value) {
      return;
    }

    parties.value.push(
      createNewParty({
        order: parties.value.length,
      }),
    );
  }

  /** @param {number} index - party index */
  function removeParty(index) {
    if (!parties.value) {
      return;
    }

    if (canRemoveParties.value) {
      parties.value.splice(index, 1);
    }
  }

  return {
    parties,
    addParty,
    removeParty,
    canRemoveParties,
  };
}
